import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Segmented, Space, theme } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import MaintenanceWindowsV2Context from '@totem/components/maintenanceWindowsV2/MaintenanceWindowsV2Context';
import { DecisionTag } from '@totem/components/maintenanceWindowsV2/StatusTags';
import {
  Approval,
  ApprovalDecision,
  MaintenanceWindow,
} from '@totem/components/maintenanceWindowsV2/types';
import {
  approvalResponseMs,
  DAY_MS,
  formatDateTime,
  formatDuration,
  getApprovalDecision,
  getBuildingLabel,
  getWindowStatus,
  startTime,
  toTime,
} from '@totem/components/maintenanceWindowsV2/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import {
  sortNumberAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

type ApprovalsView = 'mine' | 'pending' | 'all';

interface ApprovalRow {
  key: string;
  win: MaintenanceWindow;
  approval: Approval;
  decision: ApprovalDecision;
  isMine: boolean;
}

const ApprovalsTab = () => {
  const { loading, filteredData, now, sending, onAction } = useContext(
    MaintenanceWindowsV2Context,
  );
  const { userProfile } = useContext(UserProfileContext);
  const { token } = theme.useToken();
  const [view, setView] = useState<ApprovalsView>('mine');
  const [pageSize, setPageSize] = useState<number>(20);

  const allRows = useMemo(
    (): ApprovalRow[] =>
      filteredData
        .filter((win) => win.details.requiresBypass)
        .flatMap((win) =>
          (win.approvals ?? []).map((approval) => ({
            key: `${win.id}-${approval.approver}`,
            win,
            approval,
            decision: getApprovalDecision(win, approval),
            isMine:
              approval.approver.toLowerCase() ===
              userProfile.email.toLowerCase(),
          })),
        ),
    [filteredData, userProfile.email],
  );

  // A decision can still be changed until the window starts.
  const canDecide = (row: ApprovalRow) =>
    row.isMine &&
    ['awaitingApproval', 'bypassRejected', 'scheduled'].includes(
      getWindowStatus(row.win, now),
    );

  const viewRows = {
    mine: allRows.filter(
      (row) => row.isMine && row.decision === 'pending' && canDecide(row),
    ),
    pending: allRows.filter(
      (row) =>
        row.decision === 'pending' &&
        getWindowStatus(row.win, now) !== 'closed',
    ),
    all: allRows,
  };

  const secondary = { color: token.colorTextSecondary, fontSize: 12 };

  const columns: ColumnProps<ApprovalRow>[] = [
    {
      title: 'Window',
      key: 'window',
      render: (_, row) => (
        <>
          <Link to={`/dashboard/maintenanceWindows2/${row.win.id}`}>
            {row.win.details.reason || 'Maintenance window'}
          </Link>
          <div style={secondary}>{getBuildingLabel(row.win)}</div>
        </>
      ),
      sorter: (compA, compB) =>
        sortStringAscending(compA.win.details.reason, compB.win.details.reason),
    },
    {
      title: 'Starts',
      key: 'starts',
      render: (_, row) => {
        const startsIn = startTime(row.win) - now;
        return (
          <>
            {formatDateTime(row.win.details.scheduledStartTime)}
            {startsIn > 0 && (
              <div
                style={{
                  ...secondary,
                  color:
                    startsIn < DAY_MS
                      ? token.colorWarningText
                      : token.colorTextSecondary,
                }}
              >
                in {formatDuration(startsIn)}
              </div>
            )}
          </>
        );
      },
      defaultSortOrder: view === 'all' ? 'descend' : 'ascend',
      sorter: (compA, compB) =>
        sortNumberAscending(startTime(compA.win), startTime(compB.win)),
    },
    {
      title: 'Approver',
      key: 'approver',
      render: (_, row) =>
        row.isMine ? (
          <b>{row.approval.approver} (you)</b>
        ) : (
          row.approval.approver
        ),
      sorter: (compA, compB) =>
        sortStringAscending(compA.approval.approver, compB.approval.approver),
    },
    {
      title: 'Requested by',
      key: 'createdBy',
      render: (_, row) => row.win.createdBy,
    },
    {
      title: 'Requested',
      key: 'requested',
      render: (_, row) => formatDateTime(row.approval.requestedAt),
    },
    {
      title: 'Decision',
      key: 'decision',
      render: (_, row) => <DecisionTag decision={row.decision} />,
    },
    {
      title: 'Decided',
      key: 'decided',
      render: (_, row) =>
        ['approved', 'autoApproved', 'rejected'].includes(row.decision)
          ? formatDateTime(row.approval.approvedAt)
          : '-',
    },
    {
      title: 'Response time',
      key: 'response',
      align: 'right',
      render: (_, row) =>
        row.decision === 'pending' ? (
          <span style={secondary}>
            waiting {formatDuration(now - toTime(row.approval.requestedAt))}
          </span>
        ) : (
          formatDuration(approvalResponseMs(row.approval))
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      render: (_, row) =>
        canDecide(row) && (
          <Space>
            {row.approval.status !== 'Rejected' && (
              <Button
                size="small"
                danger
                disabled={sending}
                onClick={() => onAction('reject', row.win)}
              >
                Reject
              </Button>
            )}
            {row.approval.status !== 'Approved' && (
              <Button
                size="small"
                type="primary"
                disabled={sending}
                onClick={() => onAction('approve', row.win)}
              >
                Approve
              </Button>
            )}
          </Space>
        ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Segmented<ApprovalsView>
        value={view}
        onChange={setView}
        options={[
          { value: 'mine', label: `Awaiting me ${viewRows.mine.length}` },
          { value: 'pending', label: `All pending ${viewRows.pending.length}` },
          { value: 'all', label: `All requests ${viewRows.all.length}` },
        ]}
        style={{ alignSelf: 'flex-start' }}
      />
      <IBTable
        key={view}
        showSorterTooltip
        columns={columns}
        dataSource={viewRows[view]}
        loading={loading}
        rowKey={(row) => row.key}
        scroll={{ x: true }}
        locale={{
          emptyText:
            view === 'mine'
              ? 'Nothing is waiting on you'
              : 'No approval requests',
        }}
        pagination={{
          pageSize,
          showSizeChanger: true,
          onShowSizeChange: (_, size) => setPageSize(size),
        }}
      />
    </div>
  );
};

export default ApprovalsTab;
